import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import Lottie from 'lottie-react';
import {ChekinMark} from '@chekinapp/ui';
import {useLazyImport} from '@chekin/core';
import {LocaleButton} from 'components/LanguageButton';
import {useFocusHeadingAndAnnounce} from 'hooks/useFocusHeadingAndAnnounce';
import {usePath} from 'hooks/common';
import {PATHS} from 'routes/constants';

function UnattendedWelcomeView() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const {headingRef, announcement} = useFocusHeadingAndAnnounce(
    t('unattended_welcome_announce'),
  );
  const {getLazyValue: getTapScreenAnimation} = useLazyImport(
    () => import('assets/lotties/tap-screen.json'),
  );
  const tapScreenAnimation = getTapScreenAnimation();
  const prefersReducedMotion = Boolean(
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  );

  const handleStart = () => {
    navigate(getFullPath(PATHS.searchReservation.main));
  };

  return (
    <div className="relative min-h-[var(--app-dvh,100dvh)] w-full bg-[var(--background-main)]">
      <button
        type="button"
        onClick={handleStart}
        aria-label={t('unattended_welcome_subtitle')}
        className="absolute inset-0 z-0 cursor-pointer"
      />

      <div className="pointer-events-none relative z-[1] flex min-h-[var(--app-dvh,100dvh)] w-full flex-col items-center justify-center px-6">
        <ChekinMark className="h-auto w-[87px] md:w-[109px] lg:w-[131px]" />

        <h1
          ref={headingRef}
          tabIndex={-1}
          className="mt-8 text-center text-[32px] font-medium leading-[1.15] tracking-[-0.02em] text-[color:var(--chekin-color-brand-navy)] outline-none md:text-[38px] lg:text-[44px]"
        >
          {t('unattended_welcome_title')}
        </h1>

        <p className="mt-3 text-center text-[15px] leading-[1.4] text-[color:var(--chekin-text-secondary)] md:text-[16px] lg:text-[18px]">
          {t('unattended_welcome_subtitle')}
        </p>

        {tapScreenAnimation && (
          <Lottie
            animationData={tapScreenAnimation}
            loop={!prefersReducedMotion}
            autoplay={!prefersReducedMotion}
            className="mt-8 w-[180px] md:w-[210px] lg:w-[240px]"
          />
        )}
      </div>

      <LocaleButton withFlag className="absolute right-6 top-6 z-[2]" />

      <span className="sr-only" aria-live="polite">
        {announcement}
      </span>
    </div>
  );
}

export {UnattendedWelcomeView};
